import { Component, Input, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import {
  MachineGroupTriggerConfigurationEditComponent
} from '@components/administration/machine-group/machine-group-configuration/admin-machine-group-trigger-configuration/machine-group-trigger-configuration-edit/machine-group-trigger-configuration-edit.component';
import { DEFAULT_SORT_TYPE } from '@constants/sorting/default-sort-type';
import {
  AddTriggerConfigurationRequest,
  TriggerConfiguration
} from '@models/adminstration/machine/trigger/trigger-configuration';
import { Machine } from '@models/machine/machine';
import { Role } from '@models/user/role';
import { User } from '@models/user/user';
import { TranslateService } from '@ngx-translate/core';
import {
  MachineGroupTriggerConfigurationService
} from '@services/administration/machine-group/trigger/machine-group-trigger-configuration.service';
import { LoadingIndicatorService } from '@services/loading-indicator/loading-indicator.service';
import { ModalService } from '@services/modal/modal.service';
import { NotificationService } from '@services/notification/notification.service';
import { TriggerStylesService } from '@services/styles/administration/machine/trigger/trigger-styles.service';
import { TableHeaderSortableService } from '@services/table-header-sortable/table-header-sortable.service';
import { SortEvent, TableHeaderSortableDirective } from '@shared/table-header-sortable/table-header-sortable.directive';
import { ReplaySubject } from 'rxjs';
import { catchError, switchMap, takeUntil } from 'rxjs/operators';

@Component({
    selector: 'scitis-admin-machine-group-trigger-configuration',
    templateUrl: './admin-machine-group-trigger-configuration.component.html',
    styleUrls: ['./admin-machine-group-trigger-configuration.component.scss'],
    standalone: false
})
export class AdminMachineGroupTriggerConfigurationComponent implements OnInit, OnDestroy {

  @Input()
  public userRole: Role = new Role(new User());

  @Input()
  public machineGroupId: number | undefined;

  @Input()
  public machines: Machine[] = [];

  @ViewChildren(TableHeaderSortableDirective)
  public sortableHeaders: QueryList<TableHeaderSortableDirective> = new QueryList<TableHeaderSortableDirective>();

  public triggerConfigurationList: TriggerConfiguration[] = [];
  public isGapDetectionInProgress: object = {};
  public isTriggerCanBeActivate: object | undefined;

  private destroy: ReplaySubject<boolean> = new ReplaySubject<boolean>();
  private GAP_DETECTION_IN_PROGRESS_STATUS_ERROR = 911;
  private COMPONENT_NAME: string = 'Trigger Configuration Action';

  constructor(
      private modalService: ModalService,
      private tableHeaderSortableService: TableHeaderSortableService,
      private loadingIndicator: LoadingIndicatorService,
      private translation: TranslateService,
      private triggerConfigurationService: MachineGroupTriggerConfigurationService,
      private triggerStylesService: TriggerStylesService,
      private notification: NotificationService,
  ) {
  }

  ngOnInit(): void {
    this.getTriggerConfigurations();
  }

  public onColumnSort({column, direction}: SortEvent): void {
    this.sortableHeaders = this.tableHeaderSortableService.resetHeaders(this.sortableHeaders, column);
    this.triggerConfigurationList = this.tableHeaderSortableService.sortItems<TriggerConfiguration>(this.triggerConfigurationList, column, direction);
  }

  public onAddTriggerConfiguration(): void {
    this.getModalRef().then((request: AddTriggerConfigurationRequest) => {
      this.loadingIndicator.show();
      if (this.machineGroupId) {
        this.triggerConfigurationService.machineGroupAddTriggerConfiguration(this.machineGroupId, request)
        .pipe(takeUntil(this.destroy))
        .subscribe(response => {
          if (response && this.isTriggerCanBeActivate) {
            this.isTriggerCanBeActivate[response.id] = !!response.triggerQueries;
          }
          this.triggerConfigurationList = [...this.triggerConfigurationList, response];
          this.onColumnSort(DEFAULT_SORT_TYPE);
          const message = this.translation.instant('Successfully created trigger configuration');
          this.notification.success(message, this.COMPONENT_NAME, false);
          this.loadingIndicator.hide();
        }, error => {
          console.log(error);
          const message = this.translation.instant('Could not save trigger configuration.');
          this.notification.error(message, error, this.COMPONENT_NAME, true);
          this.loadingIndicator.hide();
        });
      }
    }, () => {
      this.loadingIndicator.hide();
    });
  }

  public getModalRef(triggerConfiguration = new TriggerConfiguration()): Promise<any> {
    const modalRef = this.modalService.open(MachineGroupTriggerConfigurationEditComponent, {size: 'xl'});
    modalRef.componentInstance.userRole = this.userRole;
    modalRef.componentInstance._machines = this.machines;
    modalRef.componentInstance.machineGroupId = this.machineGroupId;
    modalRef.componentInstance.triggerConfiguration = new TriggerConfiguration(triggerConfiguration);
    return modalRef.result;
  }

  public getBadgeCssClass(isActive: boolean): string {
    return this.triggerStylesService.getTriggerConfigurationBadgeClass(isActive);
  }

  public getCircleCssClass(isActive: boolean): string {
    return this.triggerStylesService.getTriggerConfigurationCircleClass(isActive);
  }

  public getActiveWord(isActive: boolean): string {
    return this.translation.instant(this.triggerStylesService.getTriggerConfigurationActiveWord(isActive));
  }

  public onDeleteConfiguration(configId: number): void {
    if (!this.machineGroupId) {
      return;
    }
    this.loadingIndicator.show();
    this.triggerConfigurationService.machineGroupDeleteConfiguration(this.machineGroupId, configId).pipe(takeUntil(this.destroy))
    .subscribe(() => {
      this.triggerConfigurationList = this.triggerConfigurationList.filter(({id}) => id !== configId);
      this.onColumnSort(DEFAULT_SORT_TYPE);
      this.loadingIndicator.hide();
    }, error => {
      console.log(error);
      this.loadingIndicator.hide();
    });
  }

  public onEditConfiguration(triggerConfiguration: TriggerConfiguration): void {
    this.getModalRef(triggerConfiguration).then((request: TriggerConfiguration) => {
      this.loadingIndicator.show();
      if (this.machineGroupId) {
        this.triggerConfigurationService.machineGroupUpdateTriggerConfiguration(this.machineGroupId, request)
        .pipe(takeUntil(this.destroy))
        .subscribe(res => {
          const idx = this.triggerConfigurationList.indexOf(triggerConfiguration);
          this.triggerConfigurationList[idx] = new TriggerConfiguration(res);
          this.onColumnSort(DEFAULT_SORT_TYPE);
          const message = this.translation.instant('Successfully updated trigger configuration');
          this.notification.success(message, this.COMPONENT_NAME, false);
          this.loadingIndicator.hide();
        }, error => {
          console.log(error);
          const message = this.translation.instant('Could not save trigger configuration.');
          this.notification.error(message, error, this.COMPONENT_NAME, true);
          this.loadingIndicator.hide();
        });
      }
    }, () => {
      this.loadingIndicator.hide();
    });
  }

  public onDeactivateTrigger(configId: number): void {
    if (this.machineGroupId) {
      this.triggerConfigurationService.machineGroupDeactivateTriggerConfig(this.machineGroupId, configId).pipe(takeUntil(this.destroy))
      .subscribe({
        next: (result: TriggerConfiguration) => {
          this.loadingIndicator.hide();
          const targetConfig = this.triggerConfigurationList.find(({id}) => id === configId);
          const updatedConfig = (result && result.id) ? result : targetConfig;
          if (updatedConfig) {
            updatedConfig.isActive = false;
            this.replaceTriggerConfigurationInList(updatedConfig);
            this.notification.success(this.translation.instant('Trigger configuration deactivated, trigger:') + ' ' + updatedConfig.name, this.COMPONENT_NAME, false);
          }
          this.refreshGapDetectionState();
        }, error: error => {
          this.loadingIndicator.hide();
          const targetConfig = this.triggerConfigurationList.find(({id}) => id === configId);
          if (targetConfig) {
            targetConfig.isActive = true;
            this.replaceTriggerConfigurationInList(targetConfig);
          }
          if (error.status === this.GAP_DETECTION_IN_PROGRESS_STATUS_ERROR) {
            this.notification.error(this.translation.instant('Could not deactivate trigger configuration. Gap detection in progress'), error, this.COMPONENT_NAME, true);
          } else {
            this.notification.error(this.translation.instant('Error deactivating trigger configuration'), error, this.COMPONENT_NAME, true);
          }
        }
      });
    }
  }

  public onActivateTrigger(configId: number): void {
    if (this.machineGroupId) {
      this.triggerConfigurationService.machineGroupActivateTriggerConfig(this.machineGroupId, configId).pipe(takeUntil(this.destroy))
      .subscribe({
        next: (result: TriggerConfiguration) => {
          this.loadingIndicator.hide();
          const targetConfig = this.triggerConfigurationList.find(({id}) => id === configId);
          const updatedConfig = (result && result.id) ? result : targetConfig;
          if (updatedConfig) {
            updatedConfig.isActive = true;
            this.replaceTriggerConfigurationInList(updatedConfig);
            this.notification.success(this.translation.instant('trigger configuration activated, trigger:') + ' ' + updatedConfig.name, this.COMPONENT_NAME, false);
          }
          this.refreshGapDetectionState();
        }, error: error => {
          this.loadingIndicator.hide();
          const targetConfig = this.triggerConfigurationList.find(({id}) => id === configId);
          if (targetConfig) {
            targetConfig.isActive = false;
            this.replaceTriggerConfigurationInList(targetConfig);
          }
          if (error.status === this.GAP_DETECTION_IN_PROGRESS_STATUS_ERROR) {
            this.notification.error(this.translation.instant('Could not activate trigger configuration. Gap detection in progress'), error, this.COMPONENT_NAME, true);
          } else {
            this.notification.error(this.translation.instant('Error activating trigger configuration'), error, this.COMPONENT_NAME, true);
          }
        }
      });
    }
  }

  private replaceTriggerConfigurationInList(updated: TriggerConfiguration | undefined): void {
    if (!updated || !updated.id) {
      return;
    }
    const index = this.triggerConfigurationList.findIndex(({id}) => id === updated.id);
    if (index > -1) {
      this.triggerConfigurationList[index] = Object.assign(new TriggerConfiguration(), updated);
      this.triggerConfigurationList = [...this.triggerConfigurationList];
      this.onColumnSort(DEFAULT_SORT_TYPE);
    }
    if (this.isTriggerCanBeActivate) {
      this.isTriggerCanBeActivate[updated.id] = !!updated.triggerQueries;
    }
  }

  private refreshGapDetectionState(): void {
    if (!this.machineGroupId) {
      return;
    }
    this.triggerConfigurationService.machineGroupGapDetection(this.machineGroupId)
    .pipe(takeUntil(this.destroy))
    .subscribe(res => {
      this.triggerConfigurationList.forEach(item => {
        this.isGapDetectionInProgress[item.id] = res.indexOf(item.id) > -1;
      });
    }, error => {
      console.log(error);
    });
  }

  private getTriggerConfigurations(): void {
    this.loadingIndicator.show();
    if (this.machineGroupId) {
      this.triggerConfigurationService.machineGroupTriggerConfiguration(this.machineGroupId)
      .pipe(takeUntil(this.destroy), switchMap(configs => {
        this.triggerConfigurationList = configs;
        this.onColumnSort(DEFAULT_SORT_TYPE);
        return this.triggerConfigurationService.machineGroupGapDetection(this.machineGroupId as number);
      }), catchError(err => {
        console.log(err);
        this.loadingIndicator.hide();
        return this.triggerConfigurationService.machineGroupGapDetection(this.machineGroupId as number);
      }))
      .subscribe(res => {
        this.isTriggerCanBeActivate = {};
        this.triggerConfigurationList.forEach(item => {
          this.isGapDetectionInProgress[item.id] = res.indexOf(item.id) > -1;
          if (this.isTriggerCanBeActivate) {
            this.isTriggerCanBeActivate[item.id] = !!item.triggerQueries;
          }
        });
        this.loadingIndicator.hide();
      }, error => {
        console.log(error);
        this.loadingIndicator.hide();
      });
    }
  }

  ngOnDestroy(): void {
    this.destroy.next(true);
    this.destroy.complete();
  }
}
